/**
 * Type-safe API client for the DefconAI Platform gateway.
 *
 * Generated from packages/contracts/openapi/platform.yaml using openapi-fetch.
 * All paths, query params, and request bodies are type-checked against the spec.
 *
 * Usage:
 *   import { platformClient } from './platform-client';
 *   const { data, error } = await platformClient.GET('/api/me');
 *
 * ADR-0017 Workstream 2: replaces handwritten fetch wrappers in client.ts.
 */
import createClient from 'openapi-fetch'
import type { paths } from './generated/platform.d.ts'
import { fetchWithTimeout } from '../lib/fetchWithTimeout'

/**
 * Platform gateway API client.
 *
 * Configured with:
 * - credentials: 'include' for httpOnly JWT cookie auth
 * - relative baseUrl (browser-only SPA — fetch() resolves against the
 *   current page origin, no need to compute it from window.location)
 * - fetchWithTimeout: detects session idle timeout (STIG V-222389)
 */
export const platformClient = createClient<paths>({
  credentials: 'include',
  headers: {
    'Content-Type': 'application/json',
  },
  fetch: fetchWithTimeout,
})

/**
 * Custom error class for API errors with status code.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Render an error response body as a human-readable message.
 *
 * Handles the three shapes the platform actually returns:
 *   - FastAPI `HTTPException` — `{ detail: "..." }`
 *   - FastAPI request validation (422) — `{ detail: [{ loc, msg }, ...] }`
 *   - Gateway ProblemDetails — `{ message }` or `{ title }`
 *
 * The 422 array is the one that matters: interpolating it into a string
 * yields "[object Object]", which is what every module 422 used to
 * surface to the user. Keeping `loc` names the offending field, which is
 * the only part of a validation error anyone can act on.
 *
 * @param error Parsed response body, or undefined when there wasn't one.
 * @param fallback Message to use when the body carries nothing readable.
 */
export function formatApiError(error: unknown, fallback: string): string {
  if (typeof error === 'string') return error || fallback
  if (!error || typeof error !== 'object') return fallback

  const body = error as {
    detail?: unknown
    message?: unknown
    title?: unknown
    error?: unknown
  }

  if (typeof body.message === 'string' && body.message) return body.message
  if (typeof body.detail === 'string' && body.detail) return body.detail

  if (Array.isArray(body.detail)) {
    const parts = body.detail
      .map((item) => {
        if (typeof item === 'string') return item
        const { loc, msg } = (item ?? {}) as { loc?: unknown; msg?: unknown }
        // Drop the leading 'body'/'query' segment — it names the request
        // part, not the field the user typed into.
        const field = Array.isArray(loc) ? loc.slice(1).join('.') : ''
        const text = typeof msg === 'string' ? msg : ''
        return field && text ? `${field}: ${text}` : text || field
      })
      .filter(Boolean)
    if (parts.length) return parts.join('; ')
  }

  if (typeof body.title === 'string' && body.title) return body.title
  // The release-bundle endpoints answer `{ error: "..." }`. Checked last so it
  // cannot shadow the shapes above; before this it fell through to `fallback`
  // and rendered as a bare status for every caller but releaseHooks' own copy.
  if (typeof body.error === 'string' && body.error) return body.error
  return fallback
}

/**
 * Typed fetch wrapper with credentials and consistent error parsing.
 *
 * Use for endpoints not yet in the generated OpenAPI spec; migrate to
 * `platformClient` + `unwrap()` once `gen:types` runs.
 *
 * The contract that matters is that a non-2xx **throws** an {@link ApiError}
 * carrying the numeric status. Returning an empty-but-successful value instead
 * is what let a 401 on the audit search endpoint render as "No events to
 * display" for three weeks (ISS-055 / #775) — a failed read and an empty store
 * must never be the same value.
 */
export async function fetchApi<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    // `...init` FIRST, then the merged headers. With the spread last, a caller
    // passing any `init.headers` replaced the whole object and silently
    // dropped Content-Type — the merge above it looked like it handled that
    // case but could never win. No caller passes headers today, so this is a
    // trap being disarmed before someone steps in it rather than a live bug.
    ...init,
    headers: { 'Content-Type': 'application/json', ...init?.headers },
  })
  if (!response.ok) {
    const body = await response.json().catch(() => null)
    throw new ApiError(
      response.status,
      formatApiError(body, `${response.status} ${response.statusText}`),
    )
  }
  // Handle 204 No Content
  if (response.status === 204) return undefined as T

  // A 2xx whose body is not JSON is a ROUTING fault, not a transport one: the
  // classic shape is the SPA's index.html served by a catch-all because the
  // /api/audit proxy route is missing, which arrives as 200 text/html. Letting
  // response.json()'s SyntaxError propagate made callers synthesise
  // `ApiError(0)`, which readErrorMessage renders as "Audit service
  // unreachable" — pointing the operator at a service that is up and
  // answering, and away from the gateway route that is actually wrong.
  // Preserve the real status so the message can be accurate.
  try {
    return (await response.json()) as T
  } catch {
    throw new ApiError(
      response.status,
      `Expected JSON but the response was not parseable (HTTP ${response.status} ` +
        `${response.headers.get('content-type') ?? 'unknown content-type'}). ` +
        'This usually means the request was answered by the web tier rather than ' +
        'the API — check the gateway route for this path.',
    )
  }
}

/**
 * Unwrap an openapi-fetch response, throwing ApiError on failure.
 *
 * Usage:
 *   const user = await unwrap(platformClient.GET('/api/me'));
 */
export async function unwrap<T>(
  promise: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  const { data, error, response } = await promise
  if (error || !response.ok) {
    const message = formatApiError(error, `API error: ${response.status} ${response.statusText}`)
    throw new ApiError(response.status, message)
  }
  return data as T
}
